<template>
  <div class="c-multi-select-wrapper">
    <div v-click-away="clickAway" :class="multiSelectClass" @click.self="ulShow">
      <span
        v-if="selection && selectionType === 'tags'"
        class="c-multi-select-selection"
        @click="ulShow"
      >
        <span v-for="(item, index) in selectedItems" :key="index" class="c-multi-select-tag">
          {{ item.text }}
          <span
            class="c-multi-select-tag-delete close"
            aria-label="Close"
            @click.stop="removeSelected(item.value)"
          >
            <span aria-hidden="true">&times;</span>
          </span>
        </span>
        <input
          v-if="search && !inline"
          ref="inputRef"
          v-model="searchInput"
          class="c-multi-select-search"
          :placeholder="inputPlaceholder"
          type="text"
          @keyup="onSearchChange($event)"
          @focus="ulShow"
          @keydown.tab="handleTabPress($event)"
        />
      </span>
      <span
        v-else-if="selection && selectionType === 'text'"
        class="c-multi-select-selection"
        @click="ulShow"
      >
        <span v-for="(item, index) in selectedItems" :key="index">
          {{ index !== 0 ? ',' : '' }} {{ item.text }}
        </span>
        <input
          v-if="search && !inline"
          ref="inputRef"
          v-model="searchInput"
          class="c-multi-select-search"
          :placeholder="inputPlaceholder"
          type="text"
          @keyup="onSearchChange($event)"
          @focus="ulShow"
          @keydown.tab="handleTabPress($event)"
        />
      </span>
      <span v-else class="c-multi-select-selection" @click="ulShow">
        <span v-if="selectedItems.length > 0">{{ selectedItems.length }} item(s) selected</span>
        <input
          v-if="search && !inline"
          ref="inputRef"
          v-model="searchInput"
          class="c-multi-select-search"
          :placeholder="inputPlaceholder"
          type="text"
          @keyup="onSearchChange($event)"
          @focus="ulShow"
          @keydown.tab="handleTabPress($event)"
        />
      </span>

      <span v-if="visible && !inline" class="toggle_button" @click="ulClose()" />

      <div v-if="visible && optionsNotEmpty" class="c-multi-select-options" :class="ulClass">
        <template v-for="(item, idx) in flatOptions">
          <div
            v-if="item.type === 'group'"
            :key="'group-' + idx"
            class="c-multi-select-option-group"
          >
            {{ item.text }}
          </div>
          <div
            v-else-if="item.visible !== false"
            :key="'option-' + idx"
            class="c-multi-select-option"
            :class="{ 'c-multi-selected': isItemSelected(item) }"
            @click.stop="onSelectItem(item)"
          >
            {{ item.text }}
          </div>
        </template>
      </div>
      <div v-else-if="visible && !optionsNotEmpty" class="c-multi-select-options">
        <div class="c-multi-select-options-empty">
          {{ optionsEmptyPlaceholder }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
const clickAwayDirective = {
  mounted(el, binding) {
    el._clickAwayHandler = (event) => {
      if (binding.value && typeof binding.value === 'function') {
        if (!el.contains(event.target)) {
          binding.value(event);
        }
      }
    };
    document.addEventListener('click', el._clickAwayHandler);
  },
  unmounted(el) {
    document.removeEventListener('click', el._clickAwayHandler);
  },
};

export default {
  name: 'MultiSelect',
  directives: {
    clickAway: clickAwayDirective,
  },
  props: {
    name: String,
    form: String,
    selected: Array,
    search: Boolean,
    select: Boolean,
    options: Array,
    inline: Boolean,
    multiple: {
      type: Boolean,
      default: true,
    },
    optionsEmptyPlaceholder: {
      type: String,
      default: 'no items',
    },
    searchPlaceholder: {
      type: String,
      default: 'Select...',
    },
    selection: {
      type: Boolean,
      default: true,
    },
    selectionType: {
      type: String,
      default: 'counter',
    },
    selectionTypeCounterText: {
      type: String,
      default: 'item(s) selected',
    },
  },
  emits: ['update'],
  data() {
    return {
      ulClass: 'hide',
      selectedItems: [],
      searchInput: '',
      visible: false,
      flatOptions: [],
      lastSelected: null,
    };
  },
  computed: {
    multiSelectClass() {
      return {
        'c-multi-select': true,
        'c-multi-select-inline': this.inline,
        'c-multi-select-multiple': this.multiple,
        'c-multi-select-selection-tags': this.selectionType === 'tags',
        'c-show': this.visible && !this.inline,
      };
    },
    inputPlaceholder() {
      let placeholder = null;
      if (this.searchInput.length === 0) {
        placeholder = this.searchPlaceholder;
      }
      if (this.selectionType === 'counter' && this.selectedItems.length > 0) {
        placeholder = `${this.selectedItems.length} ${this.selectionTypeCounterText}`;
      }
      return placeholder;
    },
    optionsNotEmpty() {
      return this.flatOptions.some((o) => o.type !== 'group' && o.visible !== false);
    },
  },
  mounted() {
    if (this.options) {
      this.rebuildFlatOptions();
    }
    if (this.selected) {
      this.initialSelected(this.selected);
    }
    if (this.inline) {
      this.ulShow();
    }
  },
  methods: {
    handleTabPress(e) {
      if (e.shiftKey) {
        this.ulClose();
      }
    },
    isItemSelected(item) {
      return this.selectedItems.some((s) => s.value === item.value);
    },
    removeSelected(toRemove) {
      this.selectedItems = this.selectedItems.filter((el) => el.value !== toRemove);
      this.rebuildFlatOptions();
      this.emitSelected();
    },
    onSelectItem(item) {
      const idx = this.selectedItems.findIndex((s) => s.value === item.value);
      if (idx === -1) {
        if (!this.multiple) {
          this.selectedItems = [];
        }
        this.selectedItems.push({ text: item.text, value: item.value });
      } else {
        this.selectedItems.splice(idx, 1);
      }
      this.emitSelected();
      this.rebuildFlatOptions();
      this.searchInput = '';
      this.$refs?.inputRef?.focus();
    },
    emitSelected() {
      this.$emit(
        'update',
        this.selectedItems.map((i) => i.value),
      );
    },
    clickAway(e) {
      if (!this.visible) return;
      if (e.target !== this.lastSelected) {
        this.ulClose();
      }
    },
    ulShow() {
      this.visible = true;
      this.ulClass = '';
      this.$refs?.inputRef?.focus();
    },
    ulClose() {
      if (this.inline) return;
      this.visible = false;
      this.ulClass = 'hide';
    },
    onSearchChange() {
      this.rebuildFlatOptions();
      this.ulShow();
    },
    rebuildFlatOptions() {
      const result = [];
      const filter = this.searchInput.toLowerCase();
      const flatten = (items) => {
        for (const item of items) {
          if (item.options) {
            result.push({ type: 'group', text: item.label || item.text, visible: true });
            flatten(item.options);
          } else {
            const visible = !filter || (item.text && item.text.toLowerCase().includes(filter));
            result.push({ type: 'item', text: item.text, value: item.value, visible });
          }
        }
      };
      if (this.options && this.options.length) {
        flatten(this.options);
      }
      this.flatOptions = result;
    },
    initialSelected(selected) {
      if (!selected || !this.options) return;
      const findItems = (items) => {
        for (const item of items) {
          if (item.options) {
            findItems(item.options);
          } else if (selected.includes(item.value)) {
            this.selectedItems.push({ text: item.text, value: item.value });
          }
        }
      };
      findItems(this.options);
    },
  },
};
</script>

<style scoped>
.c-multi-select-wrapper {
  position: relative;
}
.c-multi-select {
  position: relative;
  min-height: calc(1.5em + 0.75rem + 2px);
  padding: 0.375rem 0.75rem;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.5;
  color: #5c6873;
  background-color: #fff;
  background-clip: padding-box;
  border: 1px solid #d8dbe0;
  border-radius: 0.25rem;
  transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
}
.c-multi-select.c-show {
  border-color: #321fdb;
  box-shadow: 0 0 0 0.2rem rgba(50, 31, 219, 0.25);
}
.c-multi-select-selection {
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.25rem;
}
.c-multi-select-tag {
  display: inline-flex;
  align-items: center;
  height: 25px;
  padding: 0 0.5rem;
  margin: 2px;
  border: 1px solid #d8dbe0;
  border-radius: 0.25rem;
  background-color: #ebedef;
  color: #768192;
  font-size: 0.875rem;
  max-width: 100%;
}
.c-multi-select-tag-delete {
  margin-left: 0.25rem;
  cursor: pointer;
  font-size: 1.2rem;
  line-height: 1;
}
.c-multi-select-search {
  flex-grow: 1;
  flex-basis: 2em;
  min-width: 2em;
  max-width: 100%;
  border: none;
  outline: none;
  background: transparent;
  padding: 0;
  font-size: 1rem;
}
.c-multi-select-options {
  position: absolute;
  z-index: 1050;
  top: 100%;
  left: 0;
  right: 0;
  max-height: 200px;
  overflow-y: auto;
  margin-top: 2px;
  padding: 0.5rem 0;
  background-color: #fff;
  border: 1px solid #d8dbe0;
  border-radius: 0.25rem;
  box-shadow: 0 4px 5px 0 rgba(0, 0, 0, 0.14), 0 1px 10px 0 rgba(0, 0, 0, 0.12),
    0 2px 4px -1px rgba(0, 0, 0, 0.2);
}
.c-multi-select-options.hide {
  display: none;
}
.c-multi-select-option {
  padding: 0.4rem 0.75rem;
  cursor: pointer;
  font-size: 14px;
  color: #1a1a1c;
  background: transparent;
}
.c-multi-select-option:hover {
  background-color: #f0f1f4;
}
.c-multi-select-option.c-multi-selected {
  background-color: #e8eaff;
  color: #321fdb;
}
.c-multi-select-option-group {
  padding: 0.4rem 0.75rem;
  font-weight: 600;
  font-size: 0.875rem;
  color: #768192;
}
.c-multi-select-options-empty {
  padding: 0.75rem;
  text-align: center;
  color: #768192;
}
.toggle_button {
  position: absolute;
  display: block;
  width: 25px;
  background: transparent;
  cursor: pointer;
  height: 100%;
  right: 0;
  top: 0;
  z-index: 1051;
}
</style>
